import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { initFirebase } from '../firebase';
import { normalizePhoneNumber } from '../utils/phoneUtils';

/**
 * Authentication service
 */
class AuthService {
  constructor() {
    const { auth, db } = initFirebase();
    this.auth = auth;
    this.db = db;
  }

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User credential
   */
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error signing in:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        throw new Error('Invalid email or password. Please try again.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled. Please contact support.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed login attempts. Please try again later.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      }
      
      throw error;
    }
  }

  /**
   * Sign up with email and password
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.firstName - User first name
   * @param {string} userData.lastName - User last name
   * @param {string} userData.phone - User phone (optional)
   * @returns {Promise<Object>} User credential
   */
  async signUp({ email, password, firstName, lastName, phone = '' }) {
    try {
      // Normalize phone if provided
      const normalizedPhone = phone ? normalizePhoneNumber(phone) : '';
      const emailLower = email.toLowerCase();

      // STEP 1: Check duplicates via backend API (no authentication required)
      const apiUrl = import.meta.env.VITE_CALENDAR_API_URL || 'http://localhost:5003';
      const checkResponse = await fetch(`${apiUrl}/api/v1/users/check-duplicates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailLower,
          phone: normalizedPhone
        })
      });

      if (!checkResponse.ok) {
        throw new Error('Failed to validate user data. Please try again.');
      }

      const checkData = await checkResponse.json();
      
      if (checkData.email_exists) {
        const error = new Error('emailAlreadyInUse');
        error.code = 'emailAlreadyInUse';
        throw error;
      }
      
      if (checkData.phone_exists) {
        const error = new Error('phoneAlreadyInUse');
        error.code = 'phoneAlreadyInUse';
        throw error;
      }

      // STEP 2: Validation passed - proceed with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const authUser = userCredential.user;

      // Update user profile with display name
      await updateProfile(authUser, {
        displayName: `${firstName} ${lastName}`,
      });

      // STEP 3: Create Firestore user document
      const userDocRef = doc(this.db, 'users', emailLower);
      await setDoc(userDocRef, {
        uid: authUser.uid,
        firstName,
        lastName,
        email: emailLower,
        phone: normalizedPhone,
        role: 'end_customer',
        customerIds: [],
        booking_businesses: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return authUser;
    } catch (error) {
      console.error('Error signing up:', error);
      
      // Provide user-friendly error messages
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email address already exists. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address. Please check and try again.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Email/password sign-up is not enabled. Please contact support.');
      }
      
      // If it's already an Error object with a message, throw it as is
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Failed to create account. Please try again.');
    }
  }

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Get the current user
   * @returns {Object|null} Current user or null
   */
  getCurrentUser() {
    return this.auth.currentUser;
  }

  /**
   * Get user role and mapping
   * @param {string} email - User email
   * @returns {Promise<Object|null>} User data including role and customerIds
   */
  async getUserMapping(email) {
    try {
      const userDoc = await getDoc(doc(this.db, 'users', email.toLowerCase()));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }

  /**
   * Check if user is a business owner
   * @param {string} email - User email
   * @returns {Promise<boolean>} True if business owner
   */
  async isBusinessOwner(email) {
    try {
      const mapping = await this.getUserMapping(email);
      return mapping?.role === 'business_owner';
    } catch (error) {
      console.error('Error checking business owner status:', error);
      return false;
    }
  }
}

export const authService = new AuthService();
export default AuthService;
